/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : Authentication.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import React, { useState } from "react";
import { Login, ForgotPassword, ResetPassword } from "../../components";
import { Box } from "@mui/material";
import appMeta from "../../assets/app-meta/appMeta";
import { useDispatch, useSelector } from "react-redux";
import { Navigate } from "react-router-dom";
import styles from "./authStyle";
import {
  ForgotPasswordAction,
  LoginAction,
  ResetPasswordAction,
} from "../../redux/actions";
import { toast } from "react-toastify";
import useThrottle from "../../hooks/useThrottle";

function Authentication() {
  const [authView, setAuthView] = useState(100);
  const auth = useSelector((state) => state.authReducer);
  const dispatch = useDispatch();

  const handleAuthViewChange = (newAuthView) => {
    setAuthView(newAuthView);
  };

  const handleLogin = ({ email, password, rememberMe }) => {
    dispatch(LoginAction({ email, password, storage: rememberMe })).then(
      (res) => {
        if (res?.error) {
          toast.error(res?.msg);
        } else {
          toast.success(res?.msg);
        }
      }
    );
  };
  const throttledHandleLogin = useThrottle(handleLogin, 2500);

  const handleForgotPassword = ({ email }) => {
    dispatch(ForgotPasswordAction({ email, handleAuthViewChange })).then(
      (res) => {
        if (res?.error) {
          toast.error(res?.msg);
        } else {
          toast.success(res?.msg);
        }
      }
    );
  };
  const throttledHandleForgotPassword = useThrottle(handleForgotPassword, 2500);

  const handleResetPassword = ({ otp, password }) => {
    dispatch(ResetPasswordAction({ otp, password, handleAuthViewChange })).then(
      (res) => {
        if (res?.error) {
          toast.error(res?.msg);
        } else {
          toast.success(res?.msg);
        }
      }
    );
  };
  const throttledHandleResetPassword = useThrottle(handleResetPassword, 2500);

  if (auth.isAuthenticated) {
    return <Navigate to="/dashboard/admin-settings/profile" replace={true} />;
  }

  return (
    <Box sx={styles.container}>
      <Box sx={styles.bgImgContainer}>
        <img height={600} src="auth_bg_car.png" alt="" srcSet="" />
      </Box>
      <Box sx={styles.mainContainer}>
        {(() => {
          switch (authView) {
            case appMeta.authView.LOGIN:
              return (
                <Login
                  handleLogin={handleLogin}
                  throttledHandleLogin={throttledHandleLogin}
                  handleAuthViewChange={handleAuthViewChange}
                />
              );
            case appMeta.authView.FORGOT_PASSWORD:
              return (
                <ForgotPassword
                  handleForgotPassword={handleForgotPassword}
                  throttledHandleForgotPassword={throttledHandleForgotPassword}
                  handleAuthViewChange={handleAuthViewChange}
                />
              );
            case appMeta.authView.RESET_PASSWORD:
              return (
                <ResetPassword
                  handleResetPassword={handleResetPassword}
                  throttledHandleResetPassword={throttledHandleResetPassword}
                  handleAuthViewChange={handleAuthViewChange}
                />
              );
            default:
              return (
                <Login
                  handleLogin={handleLogin}
                  handleAuthViewChange={handleAuthViewChange}
                />
              );
          }
        })()}
      </Box>
    </Box>
  );
}

export default Authentication;
